import { Link, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useDropshipDetail, useRefreshTracking } from '@/hooks/useActive'
import { ApiError } from '@/lib/api/client'
import type { DropshipDetail as DropshipDetailData } from '@/lib/schemas/active'
import { statusBadge, carrierLabel, trackingUrl } from '@/lib/active-status'

/**
 * Read-only detail for a submitted dropship order. Renders the PERSISTED
 * submission from our DB (via GET /api/dropship/{id}) — not a live Shopify
 * rebuild. Customer email/phone are never surfaced here.
 */

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(d)
}

const TH =
  'px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap'

export default function DropshipDetail() {
  const { dropshipOrderId = '' } = useParams()
  const { data, isLoading, isError, error, refetch } = useDropshipDetail(Number(dropshipOrderId))

  return (
    <div className="space-y-4">
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading dropship order…</p>
      ) : isError ? (
        <LoadError error={error} onRetry={() => void refetch()} />
      ) : data ? (
        <DetailView order={data} />
      ) : null}
    </div>
  )
}

function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const is404 = error instanceof ApiError && error.status === 404
  return (
    <div className="space-y-2">
      <h1 className="text-lg font-semibold">Dropship order</h1>
      <p role="alert" className="text-sm text-red-600">
        {is404
          ? 'Dropship order not found.'
          : error instanceof Error
            ? error.message
            : 'Failed to load the dropship order.'}
      </p>
      <div className="flex gap-2">
        {!is404 && (
          <button
            type="button"
            onClick={onRetry}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        )}
        <Link
          to="/active"
          className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black"
        >
          ← Back to active dropships
        </Link>
      </div>
    </div>
  )
}

function DetailView({ order }: { order: DropshipDetailData }) {
  const badge = statusBadge(order.status)
  const refresh = useRefreshTracking()
  const queryClient = useQueryClient()
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/active"
          className="inline-block text-sm text-gray-600 underline hover:text-black"
        >
          ← Back to active dropships
        </Link>
        <div className="flex items-center gap-2">
          {refresh.isError && (
            <span role="alert" className="text-xs text-red-600">
              {refresh.error instanceof Error ? refresh.error.message : 'Refresh failed.'}
            </span>
          )}
          <button
            type="button"
            onClick={() =>
              refresh.mutate(order.dropship_order_id, {
                onSuccess: () =>
                  queryClient.invalidateQueries({
                    queryKey: ['dropship-detail', order.dropship_order_id],
                  }),
              })
            }
            disabled={refresh.isPending}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-semibold">Order #{order.order_number}</h1>
          {order.is_vip_order && (
            <span title="VIP" aria-label="VIP">
              ⭐
            </span>
          )}
          <span className="text-sm tabular-nums text-gray-600">{order.reference_client}</span>
          <span className={`inline-block border px-2 py-0.5 text-xs ${badge.className}`}>
            {badge.label}
          </span>
        </div>

        <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <div>
            <span className="text-gray-500">KH status: </span>
            <span className="text-gray-700">{order.kh_status ?? '—'}</span>
          </div>
          <div>
            <span className="text-gray-500">KH order ID: </span>
            <span className="text-gray-700">{order.kh_order_id ?? '—'}</span>
          </div>
          <div>
            <span className="text-gray-500">Form 5106: </span>
            <span className="text-gray-700">
              {order.form_5106_status === 'ON_FILE' ? 'On file' : 'Pending'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Submitted: </span>
            <span className="tabular-nums text-gray-700">{formatDate(order.submitted_at)}</span>
            <span className="text-gray-400"> by {order.submitted_by}</span>
          </div>
        </div>

        <TrackingsBlock order={order} />
      </header>

      <ShippingBlock order={order} />
      <ItemsTable order={order} />
      {order.internal_note && (
        <div className="border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
            Internal note
          </div>
          <p className="mt-1 text-sm whitespace-pre-wrap text-gray-700">{order.internal_note}</p>
        </div>
      )}
      {order.override && <OverrideBlock override={order.override} />}
    </div>
  )
}

function ShippingBlock({ order }: { order: DropshipDetailData }) {
  const a = order.shipping_address
  return (
    <section className="text-sm">
      <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
        Ship to (sent to KH)
      </div>
      <div className="mt-1 text-gray-700">
        <div>{a.name}</div>
        <div>{a.address1}</div>
        {a.address2 && <div>{a.address2}</div>}
        <div>
          {a.city}, {a.province_code} {a.zip}
        </div>
        <div>{a.country_code}</div>
      </div>
    </section>
  )
}

function ItemsTable({ order }: { order: DropshipDetailData }) {
  return (
    <section className="space-y-2">
      <div className="overflow-x-auto border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className={TH}>Product</th>
              <th className={TH}>SKU</th>
              <th className={TH}>EAN</th>
              <th className={TH}>Qty</th>
              <th className={TH}>Retail</th>
              <th className={TH}>Line total</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it) => (
              <tr
                key={it.shopify_line_item_id}
                className="border-b border-gray-100 last:border-b-0"
              >
                <td className="px-3 py-2">
                  {it.product_title}
                  {it.variant_title && <span className="text-gray-500"> — {it.variant_title}</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{it.sku}</td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{it.ean ?? '—'}</td>
                <td className="px-3 py-2 tabular-nums">{it.quantity}</td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600">
                  ${it.retail_price}
                </td>
                <td className="px-3 py-2 whitespace-nowrap font-medium tabular-nums">
                  ${it.line_total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-1 border border-gray-200 bg-gray-50 p-3 text-sm">
        <div className="font-medium tabular-nums">Selected total: ${order.selected_total}</div>
        <div className="text-xs tabular-nums text-gray-600">
          Dropship fee: ${order.dropship_fee} (flat per order)
        </div>
      </div>
    </section>
  )
}

function TrackingsBlock({ order }: { order: DropshipDetailData }) {
  return (
    <section className="space-y-2 text-sm">
      <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Tracking</div>
      {order.trackings.length === 0 ? (
        <p className="text-gray-400">No tracking yet</p>
      ) : (
        <ul className="space-y-0.5">
          {order.trackings.map((t) => {
            const url = trackingUrl(t.transporter, t.tracking_number)
            return (
              <li key={t.id} className="whitespace-nowrap">
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tabular-nums underline hover:text-black"
                  >
                    {t.tracking_number}
                  </a>
                ) : (
                  <span className="tabular-nums">{t.tracking_number}</span>
                )}
                <span className="ml-1 text-xs text-gray-500">{carrierLabel(t.transporter)}</span>
                {t.source === 'MANUAL' && (
                  <span className="ml-1 border border-gray-300 px-1 text-[10px] text-gray-600">
                    MANUAL
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function OverrideBlock({ override }: { override: NonNullable<DropshipDetailData['override']> }) {
  return (
    <section className="space-y-1 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
      <div className="text-xs font-semibold tracking-wide uppercase">VIP override</div>
      <p className="whitespace-pre-wrap">{override.override_note}</p>
      <p className="text-xs">
        By {override.override_by} on {formatDate(override.override_at)} · Selected total $
        {override.selected_total} · {override.customer_order_count} orders · AOV $
        {override.customer_aov}
      </p>
    </section>
  )
}
